import type { AutoVJStatus } from './autoVJ';
import type { MusicInputMirror } from './effectSync';
import type {
  MirrorState as WireMirrorState,
  PublishPayload as WirePublishPayload,
  TransportState as WireTransportState,
} from '../wire-corpus/generated';

/**
 * Who the console is acting as. Three venue roles plus the two platform-staff tiers: `staff` is the SUPER
 * ADMIN (reaches every venue, changes anything) and `support` reads every venue and changes nothing.
 * `operator` was called `member` until 2026-09-14.
 */
export type VenueRole = 'staff' | 'support' | 'owner' | 'manager' | 'operator';

/** The roles a person can be given in the console's role picker. Staff tiers are never assignable. */
export type AssignableRole = 'operator' | 'manager' | 'owner';

/** One row of a venue's team, as `listVenueMembers` returns it. Platform staff never appear. */
export interface VenueMember {
  uid: string;
  role: string | null;
  email: string | null;
  displayName: string | null;
  addedAt: string | null;
}

/**
 * One invited address that has not been taken up, as `listVenueInvites` returns it. There is no uid yet —
 * the person has not signed in — so the email is the identity, and `inviteId` is what withdraws it.
 */
export interface VenueInvite {
  inviteId: string;
  email: string | null;
  role: string | null;
  invitedAt: string | null;
  expiresAt: string | null;
}

/**
 * What `inviteVenueMember` did. `accepted` — the email already had an account, so they are a member as of
 * now; `pending` — the invite is waiting to be taken up, and the address shows on the team as such.
 */
export interface InviteOutcome {
  inviteId: string;
  status: 'accepted' | 'pending';
  uid?: string;
  alreadyMember?: boolean;
}

export interface RemoteLivePolicy {
  enabled: boolean;
}

export interface VenueSummary {
  venueId: string;
  displayName: string;
  slug?: string;
  role: Exclude<VenueRole, 'staff' | 'support'>;
  roomCount?: number;
  remoteLive?: RemoteLivePolicy;
}

export interface RoomSummary {
  roomId: string;
  displayName: string | null;
  rigs?: Array<{ rigId: string; activeRigVersionId: string; controllerId: string | null;
    binding?: { state: 'bound' | 'unassigned' } }>;
  activeRigs?: Array<{ rigId: string; rigVersionId: string; name: string | null }>;
  activeRigId?: string | null;
  activeRigVersionId?: string | null;
  activeRig?: { displayName?: string | null; deviceCount?: number } | null;
  activeModelVersionId?: string | null;
  hasModel?: boolean;
}

export interface ControllerSummary {
  deviceId: string;
  displayName?: string | null;
  status?: string;
  lastSeenAt?: string | null;
  maxIntensity?: number;
}

export interface RoomStatus {
  venueId: string;
  roomId: string;
  displayName?: string;
  activeRig?: {
    rigId: string;
    rigVersionId: string;
    finalized: boolean;
    deviceCount: number;
    universeCount: number | null;
  } | null;
  devices: ControllerSummary[];
  rigs?: Array<{ rigId: string; rigVersionId: string; controllerId: string | null;
    binding?: { state: 'bound' | 'unassigned' } }>;
  assignments: Array<{ scoreId: string; scoreVersionId: string; enabled: boolean;
    rigId?: string; oscAddressSources?: Record<string, 'score' | 'rig'> }>;
  remoteLive?: RemoteLivePolicy;
  remoteLiveLocks?: Record<string, { sessionId?: string; expiresAt?: number; deviceId?: string }>;
}

export interface OutputViewSession {
  sessionId: string;
  userUid: string;
  displayName: string;
  client: 'console' | 'wireview';
  duration: 'default' | 'extended' | 'livegate';
  state: 'AUTHORIZED' | 'ACTIVE' | 'ENDED';
  requestedAt: number;
  /** Epoch seconds of the broker's last write; the console compares it with a snapshot's arrival (listener lag). */
  updatedAt: number;
  activeAt: number | null;
  endsAt: number | null;
  endReason?: string | null;
  endedByUid?: string | null;
  endedByDisplayName?: string | null;
}

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface ElementValues {
  index: number;
  name?: string;
  values: Record<string, number>;
  colorDisplay?: RGB | null;
}

export interface FixtureValues {
  deviceId: string;
  name?: string;
  values: Record<string, number>;
  colorDisplay?: RGB | null;
  elements?: ElementValues[];
}

/** What the room says its playback is doing, generated from the controller's Go wire type. */
export type TransportState = NonNullable<WireTransportState>;

export interface ActiveEffect {
  effectId?: string;
  id?: string;
  kind?: string;
  active?: boolean;
  /** Auto VJ running status, pushed with the effect rather than queried by the console. */
  autoVJ?: AutoVJStatus | null;
  [key: string]: unknown;
}

export interface LiveState {
  activeSource: string;
  activeSourceLabel?: string;
  blackout: boolean;
  outputRateHz: number;
  fixtures: FixtureValues[];
  transport?: TransportState | null;
  effects?: ActiveEffect[];
  scene?: { sceneId?: string | null; recalledSceneId?: string | null; panelRevision?: number | null } | null;
  remoteLive?: Record<string, any>;
  stream?: Record<string, any>;
  control?: ControlHolder;
  rigRevision?: string;
  stateRevision?: number;
}

/**
 * The bounded audio readout published with the controller's presence-gated live-state mirror.
 * Parameters and model-loading detail deliberately stay out of this projection.
 */
export interface ControllerMirrorAudio {
  state?: string;
  inputId?: string;
  /** Monitor-only lifetime fields, introduced with the controller 1.7.11 audio monitor. */
  startedAt?: string;
  expiresAt?: string;
  inputSignal?: 'music' | 'signal' | 'silence' | 'none' | string;
  signalSinceMs?: number;
  peakDb?: number;
  /** Exactly 40 oldest-first [peakDb, rmsDb] pairs on controllers 1.7.11+. */
  levelHistory?: Array<[number, number]>;
  /** Number of measured pairs at the end of levelHistory; preceding pairs are unmeasured pre-roll. */
  levelHistorySamples?: number;
  renderFps?: number;
  renderTargetFps?: number;
  renderRateShortfall?: boolean;
  analysisHz?: number;
  outputHz?: number;
  latencyMs?: number;
  captureBlock?: number;
  beatTracker?: string;
  beatPhase?: number;
  bpm?: number | null;
  bpmRaw?: number | null;
  octaveHeld?: boolean;
  rangeFolded?: boolean;
  tempoRange?: number[];
  songChangeAt?: number | null;
  /** Current time on the same sidecar-session timeline as songChangeAt. */
  analysisClockS?: number;
  trackerConfidence?: number;
  beatConfidence?: number;
  meter?: number;
  clip?: boolean;
  rmsDb?: number;
  gateState?: string;
  xrunCount?: number;
  captureOverflows?: number;
  droppedHops?: number;
  captureMaxGapMs?: number;
  captureState?: string;
  captureRestarts?: number;
}

/** `music-telemetry/2`: the schema tag on the controller's leased music-telemetry node. */
export const MUSIC_TELEMETRY_SCHEMA = 'music-telemetry/2';

/**
 * `music-telemetry/2` — the room's music measurement, on its own leased node.
 *
 * The combined fields remain readable from stored `/1` data; `/2` adds channel meters in stream order.
 * `inputSignal` and `bpm` are republished only when they change; `peakDb` and `rmsDb` at most once
 * a second. It carries no level history: the ten-second graph is forty float pairs, which
 * would cost eighteen times the rest of the channel put together, and the direct `get_status.audioMonitor`
 * path does not carry it either.
 *
 * Owned by `nuance-venue-controller/docs/CONTRACT-controller-state-mirror.md` B30, and pinned on the Go
 * side by `TestMusicTelemetryWireShapeIsPinned`.
 */
export interface MusicTelemetry {
  schema?: string;
  /** The listed identity of the capture being measured, so one room cannot show another room's tempo. */
  inputId?: string;
  inputSignal?: 'music' | 'signal' | 'silence' | 'none' | string;
  /** The elected tempo. Zero means the analyzer has none — it is never null on this node. */
  bpm?: number;
  /**
   * Whether the analyzer is LOCKED onto that tempo, rather than reporting its best guess.
   *
   * The two are not the same measurement and the number alone cannot tell them apart: a fallback tracker
   * keeps a plausible bpm running through a room it has not locked onto at all. Absent on a 1.7.20-shaped
   * node, which is read as "the controller did not say" — never as "not locked".
   */
  locked?: boolean;
  peakDb?: number;
  rmsDb?: number;
  channels?: Array<{ peakDb: number; rmsDb: number; clipped: boolean }>;
}

export interface ChannelDescriptor {
  name?: string;
  key?: string;
  group?: string;
  subType?: string;
  bitWidth?: number;
  hasFine?: boolean;
  coarseOffset?: number;
  fineOffset?: number;
}

export interface ElementDescriptor {
  index: number;
  name?: string;
  channels?: ChannelDescriptor[];
  channelKeys?: string[];
}

export interface ElementTopologyDescriptor {
  count: number;
  channelTemplates: Array<{ channels: ChannelDescriptor[] }>;
  runs: Array<{
    first: number;
    count: number;
    template: number;
    namePattern?: { prefix?: string; suffix?: string; start?: number; step?: number };
  }>;
  exceptions?: Array<{
    index: number;
    name?: string;
    /** Present for a structural exception; an empty list is still a complete element shape. */
    channels?: ChannelDescriptor[];
    /** Direct-controller topology may carry a name-only override on an element covered by a run. */
    channelNames?: string[];
  }>;
}

export interface FixtureDescriptor {
  deviceId: string;
  /** MVR fixture UUID verbatim, when placed; layout comparisons lower-case it. */
  placementUid?: string;
  name?: string;
  description?: string;
  kind?: string;
  position?: { x: number; y: number; z?: number };
  representativeChannels?: ChannelDescriptor[];
  channels?: ChannelDescriptor[];
  channelKeys?: string[];
  elements?: ElementDescriptor[];
  elementTopology?: ElementTopologyDescriptor;
  elementCount?: number;
  isControllable?: boolean;
  [key: string]: unknown;
}

export interface RigSnapshot {
  projection?: string;
  rigVersionId?: string;
  /** The controller-loaded version summary, with no cloud URL. */
  schematicLayout?: { url?: string; elementCount?: number; schema?: string; sha256?: string; layoutDigest?: string } | null;
  /** The controller-loaded digest, independent of the cloud version record. */
  effectLayout?: EffectLayoutArtifact | null;
  fixtures: FixtureDescriptor[];
  groups?: Array<{ id?: string; name: string; fixtures?: string[]; deviceIds?: string[] }>;
  [key: string]: unknown;
}

export interface CloudRig {
  rigId?: string;
  activeRigVersionId: string | null;
  rigVersion: ({
    devices?: FixtureDescriptor[];
    groups?: RigSnapshot['groups'];
    layouts?: Record<string, {
      channels?: ChannelDescriptor[];
      elementTopology?: ElementTopologyDescriptor;
    }>;
  } & Record<string, unknown>) | null;
  schematicLayout?: { url?: string; elementCount?: number; schema?: string; sha256?: string; layoutDigest?: string } | null;
  /**
   * The `effect-layout/1` artifact published with the room's ACTIVE rig version, or null when that
   * version carries none (CONTRACT-effect-layout-artifact S5 — the rollout gate older rig versions sit
   * behind). `url` is a short-lived signed READ URL and is the only path to the bytes; Storage rules deny
   * direct access, so it cannot be reconstructed from `rigId`/`rigVersionId` and must be used as given.
   */
  effectLayout?: EffectLayoutArtifact | null;
}

/** The rig-version metadata for a published effect map — everything but the document itself. */
export interface EffectLayoutArtifact {
  schema?: string;
  rigId?: string;
  rigVersionId?: string;
  /** Short-lived signed READ URL for the document. */
  url?: string;
  /** sha256 hex of the stored bytes, verified after download. */
  sha256?: string;
  /** `sha256:<hex>` over the document's canonical encoding, recomputed after parse. */
  layoutDigest?: string;
  sizeBytes?: number;
  elementCount?: number;
  zoneCount?: number;
}

export interface ScoreSummary {
  scoreId: string;
  rigId?: string;
  oscAddressConflicts?: Array<{ scoreFixtureId: string; channelId: string;
    scoreAddress: string; rigAddress: string; chosen: 'score' | 'rig' }>;
  versionId?: string;
  scoreVersionId?: string;
  latestVersionId?: string;
  versionNumber?: number;
  finalizedAt?: string;
  name?: string;
  title?: string;
  durationSeconds?: number;
  latestVersion?: {
    durationSeconds?: number;
    versionNumber?: number;
    finalizedAt?: string;
  };
  enabled?: boolean;
  isCurrent?: boolean;
  assignedTo?: Array<{ venueId: string; roomId: string }>;
}

export interface SceneSummary {
  sceneId?: string;
  id?: string;
  name: string;
  scope?: string;
  fixtureCount?: number;
  /** Shared controller-owned library metadata; never sourced from browser storage. */
  favorite?: boolean;
}

/** Facts merged beside the controller's generated mirror document by the cloud or the console. */
type ControllerMirrorLocal = {
  heartbeat?: { online?: boolean; lastSeenAt?: string };
  /**
   * A separately lease-gated copy of the room's tempo and input levels, on the same terms.
   *
   * Controller 1.7.21+. It replaces `audioMonitor`, which 1.7.20 retired from this document along with
   * every other moving measurement — leaving the Music screen with no cloud source for a tempo at all.
   */
  musicTelemetry?: MusicTelemetry | null;
  /** The room's effect library, published beside the command catalog (`effect-package-list/1` items). */
  effectCatalog?: WirePublishPayload['effects'];
  activity?: ControllerActivity;
  lastCommand?: {
    id: string;
    command: string;
    authoredRevision?: number;
    /** 1.7.19 expand-window echo; 1.7.20 acknowledgements carry authoredRevision instead. */
    resultJson?: string;
    actor?: ControllerActor;
    at?: string;
  };
};

/**
 * The controller-owned cloud mirror, generated from `sync.MirrorState`, plus only facts composed by the
 * platform or console. Removing a producer field removes it here and fails every product read at tsc.
 */
export type ControllerMirror = WireMirrorState & ControllerMirrorLocal;

/** Internal assembly shape while independently revisioned mirror nodes are still arriving. */
export type ControllerMirrorPatch = Partial<ControllerMirror>;

export interface ControllerActor {
  uid?: string;
  label?: string;
  role?: VenueRole | string;
}

export interface ControllerActivity {
  id: string;
  actor?: ControllerActor;
  command: string;
  target?: string;
  phase: 'editing' | 'committed' | 'pending' | 'applied' | 'denied';
  message?: string;
  intentJson?: string;
  at?: string;
}

export interface ControlHolder {
  holder: 'network' | 'bluetooth' | string;
  since?: string;
  device?: string;
}

export interface LanHello {
  protocolVersion: number;
  deviceId: string;
  deviceName: string;
  venueId?: string;
  roomId?: string;
  roomName?: string;
  authenticated: boolean;
  pairingActive: boolean;
  activeSource: string;
  certFingerprint: string;
  lanTokenVersion: number;
  role?: VenueRole;
}

export interface LanStatus {
  version?: string;
  daemon?: Record<string, any>;
  session?: Record<string, any>;
  sync?: Record<string, any>;
  runtime?: Record<string, any>;
  lanServer?: Record<string, any>;
  rig?: Record<string, any>;
  engine?: Record<string, any>;
  output?: Record<string, any>;
  outputBinding?: Record<string, any>;
  remoteLive?: Record<string, any>;
  update?: Record<string, any>;
  stream?: Record<string, any>;
  audioMonitor?: ControllerMirrorAudio | null;
  /**
   * The same durable selection the mirror publishes. `get_status` carries it too, and a direct console —
   * or a hosted one before the mirror's first delivery — has nothing else to read it from.
   */
  musicInput?: MusicInputMirror | null;
  control?: ControlHolder;
  maxIntensity?: number;
  mode?: string;
  [key: string]: any;
}

export type RouteName = 'connect' | 'scenes' | 'scores' | 'effects' | 'music' | 'status' | 'settings';
